/**
 * Screen 5 — ranked regions by travel burden (spz-09-results-views).
 *
 * Auto-mounted at /regions by the pages-routing-convention in App.tsx.
 * Usage: /regions?trip=<uuid>
 * Standalone verification, no API, DB or login required:
 *   /regions?trip=x&fixture=1              organizer view, full fixture data
 *   /regions?trip=x&fixture=1&role=member  member view (no mutating controls)
 *   /regions?trip=x&fixture=empty          empty-state path
 *
 * Regions arrive ranked ascending by worst_burden_hours (max over households, never an
 * average). Server order is preserved verbatim.
 */
import { useCallback, useEffect, useState, type JSX } from 'react'
import { AuthProvider, RequireAuth, useMembership } from '../lib/auth'
import { TripGateFallback, useResolvedTrip } from '../lib/useResolvedTrip'
import { compareRegions } from '../lib/regionRank'
import { regionsProposeCall, regionsScoreCall } from '../lib/edgeRoutes'
import { FUNCTIONS_URL, isSupabaseConfigured, supabase } from '../lib/supabaseClient'
import { SpzStyles, apiHeaders } from '../components/WindowCard'
import { RegionCard, type RegionScore } from '../components/RegionCard'
import { RegionMap, type MapHousehold, type MapRegion } from '../components/RegionMap'
import type { HouseholdBurden } from '../components/BurdenStrip'
import type { Burden, Household, Mode, Region, RegionStatus } from '../lib/types'

/* --------------------------------------------------------------- plumbing -- */

function useQueryParams(): URLSearchParams {
  const [params, setParams] = useState(() => new URLSearchParams(globalThis.location?.search ?? ''))
  useEffect(() => {
    const onPop = () => setParams(new URLSearchParams(globalThis.location.search))
    globalThis.addEventListener('popstate', onPop)
    return () => globalThis.removeEventListener('popstate', onPop)
  }, [])
  return params
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  return fallback
}

/* --------------------------------------------------------------- fixtures -- */

const FIXTURE_LABELS: Record<string, string> = {
  'hh-carter': 'Carters',
  'hh-saltz': 'Saltzes',
  'hh-ruiz': 'Ruiz-Saltzes',
  'hh-okafor': 'Okafors',
}

/** Map fixtures, so `?fixture=1` verifies the map standalone too — no DB, no key needed
 *  for the layout (the tiles themselves still need VITE_GOOGLE_MAPS_BROWSER_KEY). */
const FIXTURE_MAP_HOUSEHOLDS: MapHousehold[] = [
  { id: 'hh-carter', label: 'Carters', lat: 38.9302, lng: -77.3439 },
  { id: 'hh-saltz', label: 'Saltzes', lat: 40.7128, lng: -74.006 },
  { id: 'hh-ruiz', label: 'Ruiz-Saltzes', lat: 42.3601, lng: -71.0589 },
  { id: 'hh-okafor', label: 'Okafors', lat: 33.749, lng: -84.388 },
]

const FIXTURE_MAP_REGIONS: MapRegion[] = [
  { region_id: 'rg-chesapeake', name: 'Chesapeake Bay — Eastern Shore', lat: 38.78, lng: -76.07 },
  { region_id: 'rg-obx', name: 'Outer Banks, NC', lat: 35.9, lng: -75.64 },
  { region_id: 'rg-seneca', name: 'Finger Lakes, NY', lat: 42.68, lng: -76.9 },
  { region_id: 'rg-acadia', name: 'Acadia / Bar Harbor, ME', lat: 44.35, lng: -68.21 },
]

const FIXTURE_REGIONS: RegionScore[] = [
  {
    region_id: 'rg-chesapeake',
    name: 'Chesapeake Bay — Eastern Shore',
    water_type: 'bay',
    status: 'active',
    worst_burden_hours: 6.5,
    per_household: [
      {
        household_id: 'hh-saltz',
        best_mode: 'drive',
        door_to_door_hours: 2,
        est_cost: 90,
        within_preference: true,
        is_worst: false,
      },
      {
        household_id: 'hh-carter',
        best_mode: 'drive',
        door_to_door_hours: 4.5,
        est_cost: 160,
        within_preference: true,
        is_worst: false,
      },
      {
        household_id: 'hh-ruiz',
        best_mode: 'rail',
        door_to_door_hours: 5.5,
        est_cost: 240,
        within_preference: true,
        is_worst: false,
      },
      {
        household_id: 'hh-okafor',
        best_mode: 'fly',
        door_to_door_hours: 6.5,
        est_cost: 780,
        within_preference: true,
        is_worst: true,
      },
    ],
  },
  {
    region_id: 'rg-obx',
    name: 'Outer Banks — Duck & Corolla',
    water_type: 'ocean',
    status: 'proposed',
    worst_burden_hours: 9.5,
    per_household: [
      {
        household_id: 'hh-carter',
        best_mode: 'drive',
        door_to_door_hours: 5,
        est_cost: 180,
        within_preference: true,
        is_worst: false,
      },
      {
        household_id: 'hh-saltz',
        best_mode: 'drive',
        door_to_door_hours: 6,
        est_cost: 210,
        within_preference: true,
        is_worst: false,
      },
      {
        household_id: 'hh-okafor',
        best_mode: 'fly',
        door_to_door_hours: 7.5,
        est_cost: 940,
        within_preference: false,
        is_worst: false,
      },
      // is_worst AND outside preference at the same time.
      {
        household_id: 'hh-ruiz',
        best_mode: 'fly',
        door_to_door_hours: 9.5,
        est_cost: 1120,
        within_preference: false,
        is_worst: true,
      },
    ],
  },
  {
    region_id: 'rg-seneca',
    name: 'Finger Lakes — Seneca',
    water_type: 'lake',
    status: 'proposed',
    worst_burden_hours: 11,
    per_household: [
      {
        household_id: 'hh-ruiz',
        best_mode: 'drive',
        door_to_door_hours: 3.5,
        est_cost: 120,
        within_preference: true,
        is_worst: false,
      },
      {
        household_id: 'hh-saltz',
        best_mode: 'drive',
        door_to_door_hours: 7,
        est_cost: 260,
        within_preference: false,
        is_worst: false,
      },
      {
        household_id: 'hh-carter',
        best_mode: 'rail',
        door_to_door_hours: 8.5,
        est_cost: 300,
        within_preference: true,
        is_worst: false,
      },
      {
        household_id: 'hh-okafor',
        best_mode: 'fly',
        door_to_door_hours: 11,
        est_cost: 860,
        within_preference: false,
        is_worst: true,
      },
    ],
  },
  {
    region_id: 'rg-acadia',
    name: 'Downeast Maine — Acadia',
    water_type: 'ocean',
    status: 'rejected',
    worst_burden_hours: 13.5,
    per_household: [
      {
        household_id: 'hh-carter',
        best_mode: 'drive',
        door_to_door_hours: 6,
        est_cost: 220,
        within_preference: true,
        is_worst: false,
      },
      {
        household_id: 'hh-ruiz',
        best_mode: 'drive',
        door_to_door_hours: 8,
        est_cost: 280,
        within_preference: false,
        is_worst: false,
      },
      {
        household_id: 'hh-saltz',
        best_mode: 'fly',
        door_to_door_hours: 10,
        est_cost: 690,
        within_preference: false,
        is_worst: false,
      },
      {
        household_id: 'hh-okafor',
        best_mode: 'fly',
        door_to_door_hours: 13.5,
        est_cost: 1240,
        within_preference: false,
        is_worst: true,
      },
    ],
  },
]

/* ------------------------------------------------------------- read path -- */

const MODES: Mode[] = ['drive', 'rail', 'fly']
const STATUSES: RegionStatus[] = ['proposed', 'active', 'rejected']

function asMode(v: unknown): Mode {
  return MODES.includes(v as Mode) ? (v as Mode) : 'drive'
}
function asStatus(v: unknown): RegionStatus {
  return STATUSES.includes(v as RegionStatus) ? (v as RegionStatus) : 'proposed'
}

type RegionRow = Pick<
  Region,
  | 'id'
  | 'name'
  | 'water_type'
  | 'status'
  | 'anchor_lat'
  | 'anchor_lng'
  | 'suggested_by_household_id'
>

/** Coordinates for the map only. Kept out of RegionScore, which is spz-08's contract. */
const regionCoords = new Map<string, { lat: number; lng: number }>()
type BurdenRow = Pick<
  Burden,
  'region_id' | 'household_id' | 'best_mode' | 'door_to_door_hours' | 'est_cost' | 'within_preference'
>

/**
 * Members' read path: assemble RegionScore from the `region` + `burden` tables through
 * RLS, which scopes both to trip members. Ranked ascending by the worst-off household.
 *
 * TODO: drop this in favour of the engine's own scored response once #18 lands
 * `src/lib/burden/types.ts` and its score route.
 */
async function readRegionsFromDb(tripId: string): Promise<RegionScore[]> {
  const { data: regionRows, error: regionErr } = await supabase
    .from('region')
    .select('id, name, water_type, status, anchor_lat, anchor_lng, suggested_by_household_id')
    .eq('trip_id', tripId)
  if (regionErr) throw new Error(regionErr.message)
  const regions = (regionRows ?? []) as RegionRow[]
  if (regions.length === 0) return []

  // Attribution: household labels for whoever suggested each region.
  const suggesterIds = [
    ...new Set(
      regions
        .map((r) => r.suggested_by_household_id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    ),
  ]
  const suggesterLabels = new Map<string, string>()
  if (suggesterIds.length > 0) {
    const { data: hh } = await supabase.from('household').select('id, label').in('id', suggesterIds)
    for (const h of (hh ?? []) as Array<{ id: string; label: string }>) {
      suggesterLabels.set(h.id, h.label)
    }
  }

  const { data: burdenRows } = await supabase
    .from('burden')
    .select('region_id, household_id, best_mode, door_to_door_hours, est_cost, within_preference')
    .in(
      'region_id',
      regions.map((r) => r.id),
    )

  const byRegion = new Map<string, HouseholdBurden[]>()
  for (const b of (burdenRows ?? []) as BurdenRow[]) {
    const list = byRegion.get(b.region_id) ?? []
    list.push({
      household_id: b.household_id,
      best_mode: asMode(b.best_mode),
      door_to_door_hours: Number(b.door_to_door_hours ?? 0),
      est_cost: Number(b.est_cost ?? 0),
      within_preference: b.within_preference !== false,
      is_worst: false,
    })
    byRegion.set(b.region_id, list)
  }

  regionCoords.clear()
  for (const r of regions) {
    if (typeof r.anchor_lat === 'number' && typeof r.anchor_lng === 'number') {
      regionCoords.set(r.id, { lat: r.anchor_lat, lng: r.anchor_lng })
    }
  }

  const scored = regions.map<RegionScore>((r) => {
    const per = byRegion.get(r.id) ?? []
    const worstHours = per.reduce((max, h) => Math.max(max, h.door_to_door_hours), 0)
    let flagged = false
    for (const h of per) {
      // Max over households — never an average. Flag exactly one chip.
      if (!flagged && h.door_to_door_hours === worstHours) {
        h.is_worst = true
        flagged = true
      }
    }
    return {
      region_id: r.id,
      name: r.name,
      water_type: r.water_type,
      status: asStatus(r.status),
      worst_burden_hours: worstHours,
      per_household: per,
      suggested_by: r.suggested_by_household_id
        ? (suggesterLabels.get(r.suggested_by_household_id) ?? 'a household')
        : null,
    }
  })
  return scored.sort(compareRegions)
}

/* ------------------------------------------------------------------- page -- */

const PAGE_CSS = `
.spz-region-list { display: flex; flex-direction: column; gap: 0.9rem; }
.spz-legend { font-size: 0.8rem; color: var(--muted); }
.spz-incomplete { font-size: 0.85rem; line-height: 1.45; padding: 0.7rem 0.85rem; border: 2px solid #b3261e; border-radius: 0.6rem; }
`

function RegionsScreen({
  params,
  tripId,
}: {
  params: URLSearchParams
  /** Resolved upstream — from `?trip=` or the user's membership. */
  tripId: string
}): JSX.Element {
  const fixtureParam = params.get('fixture')
  const fixtureRequested = fixtureParam !== null && fixtureParam !== '0'
  const fixtureEmpty = fixtureParam === 'empty'
  const fixtureRole = params.get('role') === 'member' ? 'member' : 'organizer'

  // Fixture mode never touches the network, so it never asks for a membership row.
  const { membership } = useMembership(fixtureRequested ? '' : tripId)

  const [regions, setRegions] = useState<RegionScore[]>([])
  const [labels, setLabels] = useState<Record<string, string>>({})
  const [mapHouseholds, setMapHouseholds] = useState<MapHousehold[]>([])
  // Households with no coordinates are skipped by the burden engine, so any ranking
  // shown here is computed without them.
  const [unlocated, setUnlocated] = useState<string[]>([])
  const [mapRegions, setMapRegions] = useState<MapRegion[]>([])
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [proposing, setProposing] = useState(false)
  const [scoring, setScoring] = useState(false)
  const [busyRegion, setBusyRegion] = useState<string | null>(null)
  const [cardError, setCardError] = useState<Record<string, string>>({})
  const [usingFixture, setUsingFixture] = useState(false)

  // Organizer-only controls must not render for members. The server enforces this too;
  // this is the UI half.
  const isOrganizer =
    fixtureRequested || usingFixture
      ? fixtureRole === 'organizer'
      : membership?.role === 'organizer'

  const applyFixture = useCallback(() => {
    setRegions(fixtureEmpty ? [] : FIXTURE_REGIONS)
    setLabels(FIXTURE_LABELS)
    setMapHouseholds(fixtureEmpty ? [] : FIXTURE_MAP_HOUSEHOLDS)
    setMapRegions(fixtureEmpty ? [] : FIXTURE_MAP_REGIONS)
    setUnlocated([])
    setUsingFixture(true)
    setError(null)
  }, [fixtureEmpty])

  const load = useCallback(async () => {
    setLoading(true)
    if (fixtureRequested) {
      applyFixture()
      setLoading(false)
      return
    }
    if (!tripId) {
      // Only reachable in fixture mode without ?trip=; the signed-in path resolves
      // a trip upstream and never renders this screen without one.
      setError('No trip selected.')
      setLoading(false)
      return
    }
    if (!isSupabaseConfigured) {
      applyFixture()
      setLoading(false)
      return
    }
    try {
      const [households, scored] = await Promise.all([
        supabase.from('household').select('id, label, origin_lat, origin_lng').eq('trip_id', tripId),
        readRegionsFromDb(tripId),
      ])
      const map: Record<string, string> = {}
      const pins: MapHousehold[] = []
      const missing: string[] = []
      type HRow = Pick<Household, 'id' | 'label' | 'origin_lat' | 'origin_lng'>
      for (const h of (households.data ?? []) as HRow[]) {
        map[h.id] = h.label
        // Only geocoded households can be plotted; the rest simply don't appear.
        if (typeof h.origin_lat === 'number' && typeof h.origin_lng === 'number') {
          pins.push({ id: h.id, label: h.label, lat: h.origin_lat, lng: h.origin_lng })
        } else {
          missing.push(h.label)
        }
      }
      setLabels(map)
      setMapHouseholds(pins)
      setUnlocated(missing)
      setMapRegions(
        scored
          .map((s) => {
            const c = regionCoords.get(s.region_id)
            return c ? { region_id: s.region_id, name: s.name, lat: c.lat, lng: c.lng } : null
          })
          .filter((r): r is MapRegion => r !== null),
      )
      setRegions(scored)
      setUsingFixture(false)
      setError(null)
    } catch (err) {
      if (import.meta.env.DEV) {
        applyFixture()
      } else {
        setError(errorMessage(err, 'Could not load regions.'))
      }
    } finally {
      setLoading(false)
    }
  }, [applyFixture, fixtureRequested, tripId])

  useEffect(() => {
    void load()
  }, [load])

  const propose = useCallback(async () => {
    if (usingFixture) return
    setProposing(true)
    setNotice(null)
    setError(null)
    try {
      const call = regionsProposeCall(FUNCTIONS_URL, tripId)
      const res = await fetch(call.url, {
        method: 'POST',
        headers: await apiHeaders(),
        body: call.body,
      })
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string }
      if (res.status === 429) {
        // 5-run LLM ceiling. A normal response, not a crash.
        setNotice(
          body.message ??
            body.error ??
            'Region proposal has hit its 5-run limit for this trip. Adjust the intake and ask an admin to lift the ceiling.',
        )
        return
      }
      if (res.status === 403) throw new Error('Only the organizer can propose regions.')
      if (!res.ok) throw new Error(body.error ?? `Propose failed (${res.status}).`)
      if (body.message) setNotice(body.message)
      await load()
    } catch (err) {
      setError(errorMessage(err, 'Propose failed.'))
    } finally {
      setProposing(false)
    }
  }, [load, tripId, usingFixture])

  const rescore = useCallback(async () => {
    if (usingFixture) return
    setScoring(true)
    setError(null)
    try {
      const call = regionsScoreCall(FUNCTIONS_URL, tripId)
      const res = await fetch(call.url, {
        method: 'POST',
        headers: await apiHeaders(),
        body: call.body,
      })
      if (res.status === 403) throw new Error('Only the organizer can re-score regions.')
      if (!res.ok) throw new Error(`Re-score failed (${res.status}).`)
      const body = (await res.json()) as { regions?: RegionScore[] }
      // Server order is the ranking. Do not re-sort.
      setRegions(body.regions ?? [])
    } catch (err) {
      setError(errorMessage(err, 'Re-score failed.'))
    } finally {
      setScoring(false)
    }
  }, [tripId, usingFixture])

  const setStatus = useCallback(
    async (regionId: string, status: RegionStatus) => {
      const previous = regions
      setCardError((prev) => {
        const next = { ...prev }
        delete next[regionId]
        return next
      })
      setRegions((prev) => prev.map((r) => (r.region_id === regionId ? { ...r, status } : r)))
      if (usingFixture) return
      setBusyRegion(regionId)
      const { error: updateError } = await supabase
        .from('region')
        .update({ status })
        .eq('id', regionId)
      setBusyRegion(null)
      if (updateError) {
        // RLS rejects a non-organizer write; revert the optimistic status.
        setRegions(previous)
        setCardError((prev) => ({ ...prev, [regionId]: updateError.message }))
      }
    },
    [regions, usingFixture],
  )

  const householdLabel = useCallback((id: string) => labels[id] ?? 'Unnamed household', [labels])

  const tripQuery = tripId ? `?trip=${encodeURIComponent(tripId)}` : ''

  return (
    <main className="spz">
      <SpzStyles />
      <style href="spz-regions-page" precedence="spz">
        {PAGE_CSS}
      </style>

      <header className="spz-head">
        <h1>Regions</h1>
        <p className="spz-sub">
          Ranked by the <strong>worst-off household</strong>&rsquo;s door-to-door travel —
          lowest first. No averages: one household with a brutal trip sinks a region.
        </p>
        <nav className="spz-nav">
          <a href={`/windows${tripQuery}${usingFixture ? (tripQuery ? '&' : '?') + 'fixture=1' : ''}`}>
            &larr; Date windows
          </a>
          {!usingFixture && <a href={`/intake${tripQuery}`}>Your household&rsquo;s answers</a>}
        </nav>
      </header>

      {usingFixture ? (
        <p className="spz-note">
          <strong>Fixture mode.</strong> Sample data, no API call. Viewing as{' '}
          {isOrganizer ? 'organizer' : 'member'}.
        </p>
      ) : null}

      {isOrganizer ? (
        <div className="spz-btn-row">
          <button
            type="button"
            className="spz-btn"
            onClick={() => void propose()}
            disabled={proposing || usingFixture}
          >
            {proposing ? 'Proposing…' : 'Propose regions'}
          </button>
          <button
            type="button"
            className="spz-btn spz-btn-ghost"
            onClick={() => void rescore()}
            disabled={scoring || usingFixture}
          >
            {scoring ? 'Re-scoring…' : 'Re-score'}
          </button>
        </div>
      ) : null}

      {unlocated.length > 0 && (
        <p className="spz-incomplete" role="alert">
          <strong>Ranked without {unlocated.length} of {unlocated.length + mapHouseholds.length}{' '}
          households.</strong>{' '}
          {unlocated.join(' and ')} {unlocated.length === 1 ? "hasn't" : "haven't"} given a
          location yet, so {unlocated.length === 1 ? 'their' : 'their'} travel isn&rsquo;t in
          these numbers. Since a region is judged on its worst-off household, that can only
          make these look better than they are.
        </p>
      )}

      {notice ? <p className="spz-note">{notice}</p> : null}
      {error ? <p className="spz-note">{error}</p> : null}

      {loading ? (
        <p className="spz-sub">Loading regions&hellip;</p>
      ) : regions.length === 0 ? (
        <div className="spz-empty">
          <p>
            No regions proposed yet. Propose regions to get candidates scored by travel
            burden.
          </p>
          {isOrganizer ? null : (
            <p className="spz-sub">The organizer hasn&rsquo;t proposed regions yet.</p>
          )}
        </div>
      ) : (
        <>
          <RegionMap
            households={mapHouseholds}
            regions={mapRegions}
            selectedRegionId={selectedRegionId}
            worstHouseholdId={
              regions.find((r) => r.region_id === selectedRegionId)?.per_household.find((h) => h.is_worst)
                ?.household_id ?? null
            }
            onSelectRegion={setSelectedRegionId}
          />
          <section className="spz-region-list">
            {regions.map((region) => (
              <RegionCard
                key={region.region_id}
                region={region}
                householdLabel={householdLabel}
                isOrganizer={isOrganizer}
                busy={busyRegion === region.region_id}
                error={cardError[region.region_id] ?? null}
                onActivate={(id) => void setStatus(id, 'active')}
                onReject={(id) => void setStatus(id, 'rejected')}
              />
            ))}
          </section>
          <p className="spz-legend">
            ▲ worst-off = the household with the longest door-to-door trip. ! outside
            preference = beyond that household&rsquo;s stated travel limit.
          </p>
        </>
      )}
    </main>
  )
}

function RegionsResolved({ params }: { params: URLSearchParams }): JSX.Element {
  const resolution = useResolvedTrip()
  if (resolution.kind !== 'ready') {
    return <TripGateFallback resolution={resolution} what="regions" />
  }
  return <RegionsScreen params={params} tripId={resolution.tripId} />
}

export default function RegionsPage(): JSX.Element {
  const params = useQueryParams()
  const fixtureParam = params.get('fixture')
  const fixtureRequested = fixtureParam !== null && fixtureParam !== '0'

  // Fixture mode skips both the login gate and trip resolution — it is how this screen
  // is verified with no API, database or session.
  return (
    <AuthProvider>
      {fixtureRequested ? (
        <RegionsScreen params={params} tripId={params.get('trip') ?? ''} />
      ) : (
        <RequireAuth>
          <RegionsResolved params={params} />
        </RequireAuth>
      )}
    </AuthProvider>
  )
}
